import { Canvas, useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import type { Mesh } from 'three';
import { Color, IcosahedronGeometry } from 'three';
import { prefersReducedMotion, readToken } from '../styles/tokens.js';

function Planet({ still }: { still: boolean }) {
  const mesh = useRef<Mesh>(null);

  // Flat-shaded icosahedron: the cheapest honest version of the painterly land
  // material that P5 develops properly.
  const geometry = useMemo(() => new IcosahedronGeometry(1, 3).toNonIndexed(), []);

  const colors = useMemo(
    () => ({
      ocean: new Color(readToken('ocean-deep') || '#1d3a5c'),
      land: new Color(readToken('health-0') || '#4fae72'),
      atmosphere: new Color(readToken('atmosphere') || '#86b6e0'),
    }),
    [],
  );

  useFrame((_, delta) => {
    if (still || !mesh.current) return;
    mesh.current.rotation.y += delta * 0.06;
  });

  return (
    <group rotation={[0.35, 0, 0.25]}>
      <mesh ref={mesh} geometry={geometry}>
        <meshStandardMaterial flatShading color={colors.land} roughness={0.85} metalness={0} />
      </mesh>
      <mesh scale={0.995}>
        <sphereGeometry args={[1, 48, 48]} />
        <meshStandardMaterial color={colors.ocean} roughness={0.5} />
      </mesh>
      <mesh scale={1.08}>
        <sphereGeometry args={[1, 32, 32]} />
        <meshBasicMaterial color={colors.atmosphere} transparent opacity={0.07} />
      </mesh>
    </group>
  );
}

export function LoginPlanet() {
  const still = prefersReducedMotion();

  return (
    <Canvas
      aria-hidden
      className="pointer-events-none absolute inset-0"
      camera={{ position: [0, 0, 3.2], fov: 42 }}
      dpr={[1, 2]}
      frameloop={still ? 'demand' : 'always'}
      gl={{ antialias: true }}
    >
      <ambientLight intensity={0.35} />
      <directionalLight position={[3, 2, 4]} intensity={1.4} />
      <directionalLight position={[-4, -1, -2]} intensity={0.25} />
      <Planet still={still} />
    </Canvas>
  );
}
